import { styled } from '@mui/material/styles'
import Box from '@/components/common/Box'
import {
	colors,
	focusVisible,
	typography,
	typePresets,
	spacing,
	radius,
	shadows,
	sizing,
	transitions,
} from '@/styles/designTokens'

const BaseButton = styled('button')({
	border: 'none',
	cursor: 'pointer',
	fontFamily: 'inherit',
	backgroundColor: 'transparent',
	'&:disabled': { cursor: 'not-allowed' },
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.overlayHeavy}`,
		outlineOffset: focusVisible.outlineOffset,
	},
})

export const NavBar = styled(Box)({
	backgroundColor: colors.surfaceMuted,
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
})

export const MobileSearchRow = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: spacing.sm,
	padding: `${spacing.lg}px ${spacing.lg}px 0 ${spacing.lg}px`,
	width: '100%',
	[theme.breakpoints.up('md')]: {
		display: 'none',
	},
}))

export const MobileSearchCancel = styled(BaseButton)(({ theme }) => ({
	flexShrink: 0,
	padding: `0 ${spacing.xs}px`,
	...typePresets.labelMd,
	color: colors.textPrimary,
	[theme.breakpoints.up('md')]: {
		display: 'none',
	},
}))

export const MobileSearchBar = styled(Box)({
	flex: '1 0 0',
	display: 'flex',
	alignItems: 'center',
	gap: spacing.sm,
	height: sizing.searchBarHeight,
	padding: `0 ${spacing.sm}px`,
	borderRadius: radius.searchBar,
	backgroundColor: colors.overlaySubtle,
	border: `1px solid ${colors.borderTransparent}`,
	cursor: 'text',
	transition: `background-color ${transitions.fast}, border-color ${transitions.fast}`,
	'&:focus-within': {
		backgroundColor: colors.surfaceWhite,
		borderColor: colors.textPrimary,
	},
})

export const PillsRow = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: spacing.sm,
	padding: `${spacing.lg}px ${spacing.lg}px 0`,
	minWidth: 0,
	width: '100%',
	[theme.breakpoints.up('md')]: {
		padding: `${spacing.lg}px ${spacing.lg}px`,
		maxWidth: sizing.tabletContentMaxWidth,
		marginLeft: 'auto',
		marginRight: 'auto',
	},
	[theme.breakpoints.up('lg')]: {
		padding: `${spacing.lg}px 0`,
	},
}))

export const PillsWrap = styled('nav')(({ theme }) => ({
	display: 'none',
	[theme.breakpoints.up('md')]: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		minWidth: 0,
		overflowX: 'clip',
		overflowY: 'visible',
		flex: '1 0 0',
		position: 'relative',
	},
}))

// Absolutely positioned, visibility:hidden — measures real pill widths without
// affecting layout or the accessibility tree
export const MeasureRow = styled(Box)({
	position: 'absolute',
	visibility: 'hidden',
	pointerEvents: 'none',
	display: 'flex',
	flexWrap: 'nowrap',
	alignItems: 'center',
	gap: spacing.sm,
	whiteSpace: 'nowrap',
})

export const PillButton = styled(BaseButton, {
	shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
	flexShrink: 0,
	height: sizing.pillHeightMobile,
	padding: `0 ${spacing.lg}px`,
	borderRadius: radius.pill,
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingTight,
	whiteSpace: 'nowrap',
	color: isActive ? colors.textInverse : colors.textPrimary,
	backgroundColor: isActive ? colors.brand : colors.overlaySubtle,
	transition: `color ${transitions.fast}, background-color ${transitions.fast}`,
	'&:hover': {
		backgroundColor: isActive ? colors.brand : colors.overlayMedium,
	},
	[theme.breakpoints.up('md')]: {
		height: sizing.pillHeightDesktop,
		lineHeight: typography.lineHeightLg,
	},
}))

export const MoreButton = styled(PillButton)({
	display: 'flex',
	alignItems: 'center',
	gap: spacing.xs,
	paddingLeft: spacing.lg,
	paddingRight: spacing.sm,
})

export const MoreMenu = styled(Box)({
	backgroundColor: colors.surfaceWhite,
	borderRadius: radius.card,
	boxShadow: shadows.dropdown,
	paddingTop: spacing.xs,
	paddingBottom: spacing.xs,
	minWidth: sizing.moreMenuMinWidth,
})

export const MoreMenuItem = styled(BaseButton, {
	shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ isActive }) => ({
	display: 'block',
	width: '100%',
	textAlign: 'left',
	padding: `${spacing.sm}px ${spacing.lg}px`,
	fontSize: typography.fontSizeMd,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	fontWeight: isActive ? typography.fontWeightBold : typography.fontWeightRegular,
	color: isActive ? colors.brand : colors.textPrimary,
	'&:hover': {
		backgroundColor: colors.overlaySubtle,
	},
}))

export const SearchBar = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
	flexShrink: 0,
	display: 'none',
	alignItems: 'center',
	gap: spacing.sm,
	height: sizing.searchBarHeight,
	width: sizing.searchBarWidth,
	padding: `0 ${spacing.sm}px`,
	borderRadius: radius.searchBar,
	backgroundColor: isActive ? colors.surfaceWhite : colors.overlaySubtle,
	border: isActive ? `1px solid ${colors.textPrimary}` : `1px solid ${colors.border}`,
	cursor: 'text',
	transition: `background-color ${transitions.fast}, border-color ${transitions.fast}`,
	'&:focus-within': {
		backgroundColor: colors.surfaceWhite,
		borderColor: colors.textPrimary,
	},
	[theme.breakpoints.up('md')]: {
		display: 'flex',
	},
}))

export const SearchClearButton = styled(BaseButton)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	width: sizing.searchClearButton,
	height: sizing.searchClearButton,
	padding: 0,
	borderRadius: radius.circle,
	backgroundColor: colors.overlayHeavy,
	color: colors.textInverse,
	'&:hover': {
		backgroundColor: colors.textPrimary,
	},
})

export const SearchInput = styled('input')(({ theme }) => ({
	flex: '1 1 auto',
	minWidth: 0,
	height: sizing.navIcon,
	padding: 0,
	border: 0,
	background: 'transparent',
	outline: 'none',
	fontFamily: 'inherit',
	// iOS Safari auto-zooms the viewport when an input has font-size < 16px on focus.
	// Base is 16px (mobile-first) to prevent the zoom; desktop overrides back to 14px.
	fontSize: typography.fontSizeLg,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textPrimary,
	[theme.breakpoints.up('md')]: {
		fontSize: typography.fontSizeMd,
	},
	// Suppress browser-native focus ring — the parent container handles focus styling
	'&:focus': { outline: 'none' },
	'&:focus-visible': { outline: 'none' },
	// Suppress browser-native search cancel button and decoration
	'&::-webkit-search-cancel-button': { display: 'none', appearance: 'none' },
	'&::-webkit-search-decoration': { display: 'none', appearance: 'none' },
	'&::placeholder': {
		color: colors.textSecondary,
	},
}))

export const MobilePillRow = styled('nav')(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: spacing.sm,
	flex: '1 1 0',
	minWidth: 0,
	overflowX: 'auto',
	overflowY: 'visible',
	scrollbarWidth: 'none',
	'&::-webkit-scrollbar': { display: 'none' },
	[theme.breakpoints.up('md')]: {
		display: 'none',
	},
}))

export const HamburgerButton = styled(BaseButton)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	width: sizing.iconButton,
	height: sizing.iconButton,
	padding: spacing.sm,
	borderRadius: radius.circle,
	color: colors.textPrimary,
	'&:hover': {
		backgroundColor: colors.overlaySubtle,
	},
	[theme.breakpoints.up('md')]: {
		display: 'none',
	},
}))

export const MobileSheetHeader = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: spacing.md,
	padding: spacing.lg,
	borderBottom: `1px solid ${colors.border}`,
	flexShrink: 0,
})

export const MobileSheetTitle = styled('h2')({
	flex: '1 0 0',
	margin: 0,
	fontSize: typography.fontSizeLg,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightLg,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textPrimary,
})

export const MobileSheetCloseButton = styled(BaseButton)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	width: sizing.iconButton,
	height: sizing.iconButton,
	padding: spacing.sm,
	borderRadius: radius.circle,
	color: colors.textPrimary,
	'&:hover': {
		backgroundColor: colors.overlaySubtle,
	},
})

export const MobileSheetList = styled(Box)({
	flex: '1 1 auto',
	overflowY: 'auto',
	padding: spacing.lg,
	display: 'flex',
	flexDirection: 'column',
	gap: spacing.lg,
})

export const MobileSheetItem = styled(BaseButton, {
	shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ isActive }) => ({
	display: 'block',
	width: '100%',
	textAlign: 'left',
	padding: 0,
	...typePresets.labelLg,
	color: isActive ? colors.brand : colors.textPrimary,
}))

export const MobileSheetCancel = styled(BaseButton)({
	display: 'block',
	width: '100%',
	padding: `${spacing.sm}px ${spacing.xl}px`,
	borderTop: `1px solid ${colors.divider}`,
	borderRadius: `0 0 ${radius.sheet}px ${radius.sheet}px`,
	backgroundColor: colors.brand,
	color: colors.textInverse,
	...typePresets.labelLg,
	textTransform: 'uppercase',
	textAlign: 'center',
	flexShrink: 0,
	'&:hover': {
		backgroundColor: colors.brandHover,
	},
})

export const SheetPaperSx = {
	borderRadius: `${radius.sheet}px ${radius.sheet}px 0 0`,
	maxHeight: sizing.mobileSheetMaxHeight,
	display: 'flex',
	flexDirection: 'column' as const,
}

// Shared sx objects for MUI icon components — keeps token values out of .tsx files
export const searchIconSx = {
	width: sizing.navIcon,
	height: sizing.navIcon,
	color: colors.textSecondary,
} as const

export const primaryIconSx = {
	width: sizing.navIcon,
	height: sizing.navIcon,
	color: colors.textPrimary,
} as const

export const clearIconSx = {
	width: sizing.clearIcon,
	height: sizing.clearIcon,
} as const

// Visually hidden — positions off-screen for screen reader announcements only
export const LiveRegion = styled(Box)({
	position: 'absolute',
	width: 1,
	height: 1,
	overflow: 'hidden',
	clip: 'rect(0 0 0 0)',
	whiteSpace: 'nowrap',
})
